import { supabase } from './supabase';

export type PermissionScope = 'all' | 'assigned' | 'own' | 'none';

interface PermissionResult {
  allowed: boolean;
  scope: PermissionScope;
}

interface CachedPermissions {
  role: string;
  permissions: Map<string, { enabled: boolean; scope: PermissionScope }>;
  timestamp: number;
}

let cache: CachedPermissions | null = null;
const CACHE_TTL = 60_000;

function isCacheValid(role: string): boolean {
  return !!cache && cache.role === role && Date.now() - cache.timestamp < CACHE_TTL;
}

export function invalidatePermissionsCache() {
  cache = null;
}

async function loadPermissions(role: string): Promise<CachedPermissions> {
  if (isCacheValid(role)) return cache!;

  const { data: perms } = await supabase
    .from('role_permissions')
    .select('permission_key, enabled, scope')
    .eq('user_role', role);

  const map = new Map<string, { enabled: boolean; scope: PermissionScope }>();
  for (const p of perms || []) {
    map.set(p.permission_key, {
      enabled: p.enabled,
      scope: p.scope as PermissionScope,
    });
  }

  cache = {
    role,
    permissions: map,
    timestamp: Date.now(),
  };

  return cache;
}

export async function checkPermission(
  permissionKey: string,
  role: string,
): Promise<PermissionResult> {
  try {
    if (role === 'Admin') return { allowed: true, scope: 'all' };

    const data = await loadPermissions(role);

    const perm = data.permissions.get(permissionKey);
    if (!perm || !perm.enabled) return { allowed: false, scope: 'none' };

    return { allowed: true, scope: perm.scope };
  } catch {
    return { allowed: false, scope: 'none' };
  }
}

export async function hasPermission(permissionKey: string, role: string): Promise<boolean> {
  const result = await checkPermission(permissionKey, role);
  return result.allowed;
}

export async function getUserPermissions(role: string): Promise<Map<string, PermissionScope>> {
  try {
    if (role === 'Admin') {
      const result = new Map<string, PermissionScope>();
      for (const key of Object.values(PERMISSIONS)) {
        result.set(key, 'all');
      }
      return result;
    }

    const data = await loadPermissions(role);

    const result = new Map<string, PermissionScope>();
    for (const [key, val] of data.permissions) {
      if (val.enabled) {
        result.set(key, val.scope);
      }
    }
    return result;
  } catch {
    return new Map();
  }
}

export async function isUserAssignedToProject(projectId: string, userId: string): Promise<boolean> {
  try {
    const { data } = await supabase.rpc('is_user_assigned_to_project', {
      check_user_id: userId,
      check_project_id: projectId,
    });

    return data === true;
  } catch {
    return false;
  }
}

export async function canAccessProject(
  projectId: string,
  permissionKey: string,
  role: string,
  userId: string,
): Promise<boolean> {
  const result = await checkPermission(permissionKey, role);
  if (!result.allowed) return false;

  switch (result.scope) {
    case 'all':
      return true;
    case 'assigned':
      return isUserAssignedToProject(projectId, userId);
    case 'own':
      return isUserAssignedToProject(projectId, userId);
    case 'none':
      return false;
    default:
      return false;
  }
}

export const PERMISSIONS = {
  PROJECTS_CREATE: 'projects.create',
  PROJECTS_VIEW_ALL: 'projects.view_all',
  PROJECTS_EDIT: 'projects.edit',
  PROJECTS_DELETE: 'projects.delete',
  PROJECTS_CHANGE_STATUS: 'projects.change_status',

  CONTRACTS_CREATE: 'contracts.create',
  CONTRACTS_VIEW: 'contracts.view',
  CONTRACTS_EDIT: 'contracts.edit',
  CONTRACTS_DELETE: 'contracts.delete',

  QUOTES_CREATE: 'quotes.create',
  QUOTES_VIEW: 'quotes.view',
  QUOTES_EDIT: 'quotes.edit',
  QUOTES_DELETE: 'quotes.delete',

  SALES_INVOICES_CREATE: 'sales_invoices.create',
  SALES_INVOICES_VIEW: 'sales_invoices.view',
  SALES_INVOICES_EDIT: 'sales_invoices.edit',
  SALES_INVOICES_DELETE: 'sales_invoices.delete',
  SALES_INVOICES_SEND: 'sales_invoices.send',

  TERMIJNBONNEN_SEND: 'termijnbonnen.send',

  PURCHASE_INVOICES_VIEW: 'purchase_invoices.view',
  PURCHASE_INVOICES_APPROVE: 'purchase_invoices.approve',
  PURCHASE_INVOICES_EDIT: 'purchase_invoices.edit',
  PURCHASE_INVOICES_DELETE: 'purchase_invoices.delete',

  MEERWERK_CREATE: 'meerwerk.create',
  MEERWERK_VIEW: 'meerwerk.view',
  MEERWERK_EDIT: 'meerwerk.edit',
  MEERWERK_DELETE: 'meerwerk.delete',

  DELIVERY_SCHEDULE: 'delivery.schedule',
  DELIVERY_FILL_FORMS: 'delivery.fill_forms',
  DELIVERY_APPROVE: 'delivery.approve',

  DOCUMENTS_UPLOAD: 'documents.upload',
  DOCUMENTS_VIEW: 'documents.view',
  DOCUMENTS_DELETE: 'documents.delete',

  USERS_MANAGE: 'users.manage',
  USERS_CHANGE_ROLES: 'users.change_roles',

  SETTINGS_COMPANY: 'settings.company',
  SETTINGS_EMAIL: 'settings.email',
  SETTINGS_NOTIFICATIONS: 'settings.notifications',
  SETTINGS_PERMISSIONS: 'settings.permissions',
  /** Standaardprijzen, tijdsnormen, uurtarieven, staartkosten en bestek-mapping wijzigen (Instellingen). Prijs in een offerte valt hier niet onder. */
  TARIEVEN_BEHEER: 'tarieven.beheer',

  FINANCIAL_REGISTER_PAYMENTS: 'financial.register_payments',
  FINANCIAL_VIEW_OVERVIEW: 'financial.view_overview',
  FINANCIAL_MANAGE_BUDGET: 'financial.manage_budget',
  FINANCIAL_BANK_TRANSACTIONS: 'financial.bank_transactions',
  FINANCIAL_BATCH_PAYMENTS: 'financial.batch_payments',
  /** Opschonen (blok D, Exact-vergelijk) vanuit de afwerk-hub — Admin én Administratie (ronde 3 verkoop, Tom 04-11). */
  FINANCIAL_OPSCHONEN: 'financial.opschonen',

  LEADS_CREATE: 'leads.create',
  LEADS_VIEW: 'leads.view',
  LEADS_EDIT: 'leads.edit',
  LEADS_DELETE: 'leads.delete',
  LEADS_CONVERT: 'leads.convert',
  LEADS_ASSIGN: 'leads.assign_adviseur',
  APPOINTMENTS_CREATE: 'appointments.create',
  APPOINTMENTS_VIEW: 'appointments.view',
  OPNAME_CREATE: 'opname.create',
  OPNAME_VIEW: 'opname.view',
  CALCULATIE_CREATE: 'calculatie.create',
  CALCULATIE_VIEW: 'calculatie.view',

  AI_TRAINING: 'ai.training',
  AI_CHAT: 'ai.chat',
  AI_KNOWLEDGE_MANAGE: 'ai.knowledge_manage',

  EMAIL_INBOX_FULL: 'email.inbox_full',
  EMAIL_ASSIGN: 'email.assign',
  EMAIL_SLA_DASHBOARD: 'email.sla_dashboard',
  EMAIL_AI_ASSIST: 'email.ai_assist',
  EMAIL_PERSONAL_INBOX: 'email.personal_inbox',

  COMMUNICATIONS_VIEW: 'communications.view',

  ROLSTEIGER_VIEW: 'rolsteiger.view',
  ROLSTEIGER_CREATE: 'rolsteiger.create',
  ROLSTEIGER_MANAGE: 'rolsteiger.manage',
  ROLSTEIGER_VOORRAAD: 'rolsteiger.voorraad',
} as const;
